import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus transition-shadow";

export const WhatsAppSettings = () => {
  const [form, setForm] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/settings/whatsapp").then((r) => setForm(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!form) return null;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/whatsapp", { account_sid: form.account_sid, auth_token: form.auth_token, from_number: form.from_number, sms_from: form.sms_from, verify_sid: form.verify_sid, enabled: form.enabled });
      setForm(data);
      toast.success(data.sms_configured ? "Saved — SMS OTP is now active" : data.configured ? "WhatsApp settings saved — alerts are on" : "Saved. Add SID, token and a sender / Verify service to enable alerts");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    setBusy(true);
    try {
      await api.post("/admin/settings/whatsapp/test", { phone: testPhone });
      toast.success("Test WhatsApp sent — check the phone");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-[#0A0A0A] border border-white/10 p-8 space-y-5 lg:col-span-2" data-testid="whatsapp-settings-form">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <MessageCircle size={14} className="text-[#25D366]" />
          <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">Automated WhatsApp alerts (Twilio)</p>
        </div>
        <span data-testid="whatsapp-configured-badge" className={`font-accent text-[9px] tracking-[0.15em] uppercase border px-2 py-1 ${form.configured ? "text-emerald-400 border-emerald-400/40" : "text-yellow-400 border-yellow-400/40"}`}>
          {form.configured ? (form.enabled ? "Active" : "Paused") : "Not configured"}
        </span>
      </div>
      <p className="text-xs text-white/50 leading-relaxed">
        Investors get a WhatsApp message when an investment is approved, rejected or paid out, on KYC decisions and 3 days before maturity. Paste your Twilio credentials from{" "}
        <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline">console.twilio.com</a>. For testing use the sandbox sender <span className="font-mono text-white/70">+14155238886</span> (investors must first send "join &lt;your-sandbox-word&gt;" to it).
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="twilio-sid-input" placeholder="Account SID (AC…)" value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} className={`${inputCls} font-mono`} />
        <input data-testid="twilio-token-input" type="password" placeholder="Auth token" value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} className={`${inputCls} font-mono`} autoComplete="off" />
        <input data-testid="twilio-from-input" placeholder="WhatsApp sender (+14155238886)" value={form.from_number} onChange={(e) => setForm({ ...form, from_number: e.target.value })} className={`${inputCls} font-mono`} />
        <input data-testid="twilio-sms-from-input" placeholder="SMS sender number for OTP (+1…)" value={form.sms_from || ""} onChange={(e) => setForm({ ...form, sms_from: e.target.value })} className={`${inputCls} font-mono`} />
        <input data-testid="twilio-verify-sid-input" placeholder="Verify Service SID (VA…) — best for India OTP" value={form.verify_sid || ""} onChange={(e) => setForm({ ...form, verify_sid: e.target.value })} className={`${inputCls} font-mono sm:col-span-2`} />
      </div>
      <p className="text-xs text-white/40 leading-relaxed" data-testid="twilio-india-hint">
        For Indian mobiles, create a <span className="text-white/70">Verify</span> service in Twilio Console → Verify → Services and paste its SID above. Twilio then sends DLT-approved OTP SMS to +91 numbers — no separate sender number or template registration needed.
        <span className={`ml-2 font-accent text-[9px] tracking-[0.15em] uppercase border px-2 py-0.5 ${form.sms_configured ? "text-emerald-400 border-emerald-400/40" : "text-yellow-400 border-yellow-400/40"}`} data-testid="sms-otp-status-badge">{form.sms_configured ? "SMS OTP active" : "OTP via email until configured"}</span>
      </p>
      <label className="flex items-center gap-3 text-xs text-white/60">
        <input data-testid="twilio-enabled-checkbox" type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="accent-[#D4AF37]" /> Send WhatsApp alerts automatically
      </label>
      <div className="flex flex-wrap gap-3 items-center">
        <button data-testid="twilio-save-button" disabled={busy} className="border border-[#D4AF37] text-[#D4AF37] px-6 py-3 font-accent text-[10px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50">Save Settings</button>
        <input data-testid="twilio-test-phone-input" placeholder="Test phone (+91…)" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} className="bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus w-48" />
        <button type="button" data-testid="twilio-test-button" disabled={busy || !testPhone} onClick={test} className="border border-[#25D366]/60 text-[#25D366] px-5 py-3 font-accent text-[10px] tracking-[0.2em] uppercase hover:bg-[#25D366] hover:text-black transition-colors disabled:opacity-50">Send Test</button>
      </div>
    </form>
  );
};
